import { cloneObject, getAppBaseUrl } from '~/utils/commonUtils.ts';
import { AuthMethodData } from '~/api/types/authMethodData.ts';
import logger from '~/utils/logger';
import { CustomizationSettings } from '~/api/types/customisationData.ts';

enum PREVIEW_IFRAME_MESSAGES {
  'AUTH_METHODS' = 'AUTH_METHODS',
  'CUSTOMIZATIONS' = 'CUSTOMIZATIONS',
  'BRANDING_SETTINGS' = 'BRANDING_SETTINGS'
}

enum PARENT_NOTIFICATION_MESSAGES {
  'SEND_AUTH_METHODS' = 'SEND_AUTH_METHODS',
  'SEND_CUSTOMIZATIONS' = 'SEND_CUSTOMIZATIONS',
  'SEND_BRANDING_SETTINGS' = 'SEND_BRANDING_SETTINGS'
}

function sendMessageToIframe(
  iframe: HTMLIFrameElement,
  data: Record<string, any>
) {
  const payload = cloneObject(data);
  logger.info(`Sending message to iframe`, payload);
  iframe?.contentWindow?.postMessage(payload, getAppBaseUrl());
}

function sendMessageToParent(data: Record<string, any>) {
  window.parent.postMessage(data, getAppBaseUrl());
}

function askAuthMethodsFromParent() {
  sendMessageToParent({
    event: PARENT_NOTIFICATION_MESSAGES.SEND_AUTH_METHODS
  });
}

function askCustomizationFromParent() {
  sendMessageToParent({
    event: PARENT_NOTIFICATION_MESSAGES.SEND_CUSTOMIZATIONS
  });
}

function askBrandingSettingsFromParent() {
  sendMessageToParent({
    event: PARENT_NOTIFICATION_MESSAGES.SEND_BRANDING_SETTINGS
  });
}

function sendAuthMethods(
  iframe: HTMLIFrameElement,
  authMethods: AuthMethodData[]
) {
  sendMessageToIframe(iframe, {
    event: PREVIEW_IFRAME_MESSAGES.AUTH_METHODS,
    data: {
      authMethods
    }
  });
}

function sendCustomizationSettings(
  iframe: HTMLIFrameElement,
  customziationSettings: CustomizationSettings
) {
  sendMessageToIframe(iframe, {
    event: PREVIEW_IFRAME_MESSAGES.CUSTOMIZATIONS,
    data: {
      customziationSettings
    }
  });
}

function sendBrandingSettings(
  iframe: HTMLIFrameElement,
  customBrandingData: {
    customBranding: boolean;
  }
) {
  sendMessageToIframe(iframe, {
    event: PREVIEW_IFRAME_MESSAGES.BRANDING_SETTINGS,
    data: {
      customBrandingData
    }
  });
}

const { startListeningToEvents, stopListeningToEvents, registerPreviewEvent } =
  (() => {
    let registeredEvents: Partial<Record<PREVIEW_IFRAME_MESSAGES, Function[]>> =
      {};
    const allEventTypes = [
      ...Object.values(PREVIEW_IFRAME_MESSAGES),
      ...Object.values(PARENT_NOTIFICATION_MESSAGES)
    ];

    const onMessageHandler = function (event: MessageEvent) {
      if (event.origin !== getAppBaseUrl()) {
        logger.error('Message from untrusted origin:', event.origin);
        return;
      }

      // Process the message
      logger.info('Message received:', event.data);
      const { event: eventName, ...payLoad } = event.data;
      if (allEventTypes.includes(eventName)) {
        registeredEvents?.[eventName].forEach(
          (callback: (data: Record<string, any>) => void) => callback(payLoad)
        );
      }
    };

    return {
      startListeningToEvents: () => {
        // Listen for messages from the parent
        window.addEventListener('message', onMessageHandler, false);
      },
      stopListeningToEvents: () => {
        // Stop listening for messages from the parent
        window.removeEventListener('message', onMessageHandler, false);
        registeredEvents = {};
      },
      registerPreviewEvent: function (
        eventName: PREVIEW_IFRAME_MESSAGES | PARENT_NOTIFICATION_MESSAGES,
        callback: (data: Record<string, any>) => void
      ) {
        logger.info(`Registering event: ${eventName}`);
        if (registeredEvents[eventName]) {
          registeredEvents[eventName]?.push(callback);
        } else {
          registeredEvents[eventName] = [callback];
        }
      }
    };
  })();

export {
  PREVIEW_IFRAME_MESSAGES,
  PARENT_NOTIFICATION_MESSAGES,
  sendMessageToIframe,
  sendMessageToParent,
  askAuthMethodsFromParent,
  askCustomizationFromParent,
  askBrandingSettingsFromParent,
  sendBrandingSettings,
  sendAuthMethods,
  sendCustomizationSettings,
  startListeningToEvents,
  stopListeningToEvents,
  registerPreviewEvent
};
